/* global toastr $ */
import React, { Component } from 'react'
import {
  format_datetime,
  uploadFile,
  request,
  downloadFile,
} from '../smart_bps/lib/utils'
import { SigurPopover } from './SigurPopover'
import AttrInput from '../smart_bps/lib/attr_input'
import { store } from '../index'
import Swal from 'sweetalert2'

/**
 * Блок «Файлы» для всего проекта СИГУР (кастомная вёрстка в конфигурации sigur_cs).
 * API совместим с FileListBlock из smart_bps: project_name, object_id, files_schema, files, fileUploaded, getChoice.
 *
 * viewOnly — только просмотр/скачивание: без загрузки, без галочек и «Выбрать все»,
 * секции типов без файлов не показываются.
 */
function FilePreviewModal({ file, getChoice }) {
  if (!file) return null
  let content
  if (file.view_mode === 'img') {
    content = <img src={file.url} alt="" className="sigur-files__preview-img" />
  } else if (file.view_mode === 'pdf') {
    content = <iframe src={file.url} title={file.file_name} className="sigur-files__preview-iframe" />
  } else {
    content = <div className="sigur-files__preview-fallback">Просмотр недоступен для типа {file.view_mode}</div>
  }
  const authorLabel = getChoice ? getChoice('User', file.author_id) : String(file.author_id)
  return (
    <div className="modal fade" id="modal_file" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title text-break">{file.file_name}</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Закрыть" />
          </div>
          <div className="modal-body">
            {content}
            <div className="sigur-files__preview-meta mt-2 small text-secondary">
              <div>Дата загрузки: {format_datetime(file.d_create)}</div>
              <div>Загрузил: {authorLabel}</div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
              <i className="ti ti-square-rounded-x me-1" aria-hidden />
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export class SigurFilesBlock extends Component {
  constructor(props) {
    super(props)
    this.state = {
      blockExpanded: true,
      expandedTypes: (this.props.files_schema || [])
      .filter((file_schema) =>
        (this.props.files || []).some(
          (file) => file.file_type === file_schema.filetype
        )
      )
      .map((file_schema) => file_schema.filetype),
      selectedFiles: [],
      previewFileId: null,
      requiredPopoverKey: null,
      filesFields: this.setFilesFields(this.props.files),
      errorEditedFields: {},
      dragOverType: null,
      existingFiltetypes: (this.props.files_schema || []).map((file_schema) => file_schema.filetype),
    }
    this.dragCounter = 0
    this.refFileInput = React.createRef()
    this.getFilesByType = this.getFilesByType.bind(this)
    this.toggleBlockExpand = this.toggleBlockExpand.bind(this)
    this.toggleExpanded = this.toggleExpanded.bind(this)
    this.uploadHandler = this.uploadHandler.bind(this)
    this.downloadFiles = this.downloadFiles.bind(this)
    this.toggleSelectAll = this.toggleSelectAll.bind(this)
    this.toggleFileSelect = this.toggleFileSelect.bind(this)
    this.toggleTypeSelect = this.toggleTypeSelect.bind(this)
    this.openPreview = this.openPreview.bind(this)
    this.deleteFile = this.deleteFile.bind(this)
    this.renderFile = this.renderFile.bind(this)
    this.renderTypeSection = this.renderTypeSection.bind(this)
    this.getIsValid = this.getIsValid.bind(this)
    this.renderValidityIcon = this.renderValidityIcon.bind(this)
    this.handleFileFieldChange = this.handleFileFieldChange.bind(this)
    this.getFileFieldValue = this.getFileFieldValue.bind(this)
    this.saveAllEditedFileDates = this.saveAllEditedFileDates.bind(this)
    this.setFilesFields = this.setFilesFields.bind(this)
  }

  setFilesFields(data) {
    if (!Array.isArray(data)) data = [data]
    const schemaMap = (this.props.files_schema || []).reduce((acc, schema) => {
      acc[schema.filetype] = schema
      return acc
    }, {})

    return (data || []).reduce((acc, file) => {
      const schema = schemaMap[file.file_type]

      acc[file.id] = {
        ...file,
        file_title: schema?.title ?? file.file_title,
        is_required: schema?.is_required ?? file.is_required,
      }

      return acc
    }, {})
  }

  getSchemaByFiletype(filetype) {
    return (this.props.files_schema || []).find((s) => s.filetype === filetype)
  }

  getDateFieldFlags(schema) {
    const dateTypes = schema?.date_types || []
    return {
      showDateStart: !!(schema?.track_validity_period && dateTypes.includes('date_start')),
      showDateEnd: !!(schema?.track_validity_period && dateTypes.includes('date_end')),
    }
  }

  getIsValid(docTypeId) {
    const schemaItem = (this.props.files_schema || []).find(
      (d) => d.doc_type_id === docTypeId
    )
    if (!schemaItem?.track_validity_period) return null

    const { showDateStart, showDateEnd } = this.getDateFieldFlags(schemaItem)
    if (!showDateStart && !showDateEnd) return null

    const filesOfType = (this.props.files || []).filter(
      (f) => f.file_type === schemaItem.filetype
    )

    const hasAnyDate = filesOfType.some(
      (f) => (showDateStart && f.d_start) || (showDateEnd && f.d_end)
    )
    if (!hasAnyDate) return null

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const isValid = filesOfType.every((f) => {
      if (showDateStart && showDateEnd) {
        if (!f.d_start || !f.d_end) return false
        const start = new Date(f.d_start)
        const end = new Date(f.d_end)
        start.setHours(0, 0, 0, 0)
        end.setHours(0, 0, 0, 0)
        return start <= today && today <= end
      }
      if (showDateStart && f.d_start) {
        const start = new Date(f.d_start)
        start.setHours(0, 0, 0, 0)
        return start <= today
      }
      if (showDateEnd && f.d_end) {
        const end = new Date(f.d_end)
        end.setHours(0, 0, 0, 0)
        return today <= end
      }
      return false
    })

    return isValid
  }

  renderValidityIcon(isValid) {
    if (isValid == null) return null
    if (isValid === true) {
      return (
        <span className="docs-tab-validity--ok ms-2 d-inline-flex align-items-center" title="Документ действующий">
          <i className="ti ti-circle-check" aria-hidden />
        </span>
      )
    }

    return (
      <span className="docs-tab-validity--warn ms-2 d-inline-flex align-items-center" title="Документ недействующий">
        <i className="ti ti-alert-circle" aria-hidden />
      </span>
    )
  }

  getFilesByType(filetype, files) {
    const list = files ?? this.getVisibleFiles(this.props.files)
      if (filetype === 'other_files') {
          // Если вкладка "Другие файлы", то возвращаем файлы, чей file_type НЕ входит в существующие типы файлов.
          return list.filter((v) => !this.state.existingFiltetypes.includes(v.file_type))
      }
    return list.filter((v) => v.file_type === filetype)
  }

  toggleBlockExpand() {
    this.setState((s) => ({ blockExpanded: !s.blockExpanded }))
  }

  toggleExpanded(filetype) {
    const typeItems = (this.props.files_schema || []).filter((i) => i.filetype !== undefined)
    const allTypes = typeItems.map((i) => i.filetype)
    this.setState((s) => {
      const expanded = s.expandedTypes === null ? allTypes : s.expandedTypes
      const isCurrentlyExpanded = expanded.includes(filetype)
      if (isCurrentlyExpanded) {
        const next = expanded.filter((t) => t !== filetype)
        return { expandedTypes: next.length === 0 ? [] : next }
      }
      const next = expanded.length === 0 ? [filetype] : [...expanded, filetype]
      return { expandedTypes: next.length === allTypes.length ? null : next }
    })
  }

  uploadHandler(fileType, directFiles = null) {
    const processFiles = (files) => {
      Array.from(files || []).forEach((fileItem) => {
        uploadFile({
          file_item: fileItem,
          url: `/api/${this.props.project_name}/${this.props.object_id}/upload_file`,
          form_args: { file_type: fileType },
          callback: (data) => {
            this.props.fileUploaded(data)
            let expandedTypes = [...this.state.expandedTypes]
            expandedTypes.push(fileType)

            this.setState(prevState => ({
              filesFields: {
                ...(prevState.filesFields || {}),
                ...this.setFilesFields(data),
              },
              expandedTypes: expandedTypes
            }))
          },
        })
      })
    }

    if (directFiles) {
      processFiles(directFiles)
      return
    }

    const input = this.refFileInput.current
    if (!input) return

    const onChange = () => {
      processFiles(input.files)

      input.value = ''
      input.removeEventListener('change', onChange)
    }

    input.addEventListener('change', onChange, { once: true })
    input.click()
  }

  downloadFiles(...items) {
    if (items.length === 0) {
      toastr.warning('Не выбрано ни одного файла для скачивания. Отметьте хотя бы один')
      return
    }

    request({
      method: 'GET',
      url: `/api/${this.props.project_name}/${this.props.object_id}/download_files?${items.map((i) => `files_ids=${i.id}`).join('&')}`,
      responseType: 'file',
      success: (data) => {
        let filename = data.filename;

        try {
          if (filename && typeof filename === 'string' && filename.includes('%')) {
            filename = decodeURIComponent(filename);
          }
        } catch (e) {
          console.warn('Не удалось декодировать имя файла:', e);
        }

        downloadFile(data.content, filename);
      },
    })
  }

  toggleSelectAll() {
    const { files } = this.props
    const { selectedFiles } = this.state
    const allSelected = files.length > 0 && selectedFiles.length === files.length
    this.setState({ selectedFiles: allSelected ? [] : [...files] })
  }

  toggleFileSelect(file) {
    this.setState((s) => {
      const has = s.selectedFiles.some((f) => f.id === file.id)
      return {
        selectedFiles: has
          ? s.selectedFiles.filter((f) => f.id !== file.id)
          : [...s.selectedFiles, file],
      }
    })
  }

  toggleTypeSelect(filetype) {
    const list = this.getFilesByType(filetype)
    if (list.length === 0) return
    this.setState((s) => {
      const ids = list.map((f) => f.id)
      const allSelected = ids.every((id) => s.selectedFiles.some((f) => f.id === id))
      return {
        selectedFiles: allSelected
          ? s.selectedFiles.filter((f) => !ids.includes(f.id))
          : [...s.selectedFiles, ...list.filter((f) => !s.selectedFiles.some((x) => x.id === f.id))],
      }
    })
  }

  openPreview(file) {
    this.setState({ previewFileId: file.id }, () => {
      if (typeof $ !== 'undefined' && $('#modal_file').length) {
        $('#modal_file').modal('show')
      }
    })
  }

  deleteFile(file_ids) {
    Swal.fire({
      title: 'Удалить файл?',
      text: 'Файл будет удалён',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Удалить',
      cancelButtonText: 'Отмена',
      focusConfirm: false,
      focusCancel: false,
      customClass: {
        confirmButton: 'btn btn-outline-primary',
        cancelButton: 'btn btn-outline-secondary',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        request({
          method: 'POST',
          url: `/api/${this.props.project_name}/${this.props.object_id}/delete_file`,
          data: { file_ids },
          success: (data) => {
            store.dispatch({type: `${this.props.project_name}_list_clean`})
            this.props.documetSetState(data)
            const idsToDelete = new Set((file_ids || []).map(id => String(id)));
            this.setState(prevState => {
              const currentFiles = prevState.filesFields || {};
              const updatedFiles = Object.fromEntries(
                Object.entries(currentFiles).filter(([key]) => !idsToDelete.has(key))
              );
              return { filesFields: updatedFiles };
            });
          },
        })
      }
    })
  }

  handleFileFieldChange = (fileId, field, value) => {
    this.setState(prev => {
      const prevFileEdits = prev.filesFields?.[fileId] || {}
      return {
        filesFields: {
          ...prev.filesFields,
          [fileId]: {
            ...prevFileEdits,
            [field]: value
          }
        },
      }
    })
  }

  getFileFieldValue = (item, field) => {
    const edited = this.state.filesFields?.[item.id]
    if (edited && field in edited) {
      return edited[field]
    }
    return item[field] ?? ''
  }

  saveAllEditedFileDates() {
    const { existingFiltetypes, filesFields } = this.state

    const fileIds = Object.keys(filesFields || {})
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
    if (!fileIds.length) return Promise.resolve({'success': true})

    const { project_name, object_id } = this.props

    const items = fileIds.map((id) => {
      const edits = filesFields[id]
      const schema = this.getSchemaByFiletype(edits.file_type)
      if (!schema?.track_validity_period) return null
      const { showDateStart, showDateEnd } = this.getDateFieldFlags(schema)
      if (!showDateStart && !showDateEnd) return null
      const item = {
        file_id: id,
        file_type: existingFiltetypes.includes(edits.file_type) ? edits.file_type : 'other_files',
      }
      if (showDateStart && 'd_start' in edits) {
        item.d_start = edits.d_start
      }
      if (showDateEnd && 'd_end' in edits) {
        item.d_end = edits.d_end
      }
      if (!('d_start' in item) && !('d_end' in item)) return null
      return item
    }).filter(Boolean)
    
    return new Promise((resolve) => {
      request({
        method: 'post',
        url: `/api/${project_name}/${object_id}/update_file_fields`,
        data: { items },
        success: (data) => {
          if (data.errors) {
            const fileTitles = [...new Set(
              Object.keys(data.errors)
                .map(fileId => this.state.filesFields?.[fileId]?.file_title)
                .filter(Boolean)
            )].map(title => `"${title}"`)
            const toastrMsg = `Изменения не были сохранены. Пожалуйста, заполните обязательные даты для документов ${fileTitles.join(', ')}`
            this.setState({errorEditedFields: data.errors})
            resolve({
            'success': false,
            'toastrMsg': toastrMsg,
            })
          } else {
            const saved = (data && data.items) || []
            this.setState({errorEditedFields: {}})
            if (Array.isArray(saved) && this.props.onFileDateSaved) {
              saved.forEach((item) => this.props.onFileDateSaved(item))
            }
            resolve({
            'success': true,
            'hasToastr': false,
            })
          }
        },
        error: () => {
          toastr.error('Не удалось сохранить даты файлов')
          resolve({
          'success': false,
          })
        },
      })
    })
  }

  canDownloadFile(item) {
    const fileAccess = this.getFileAccess(item?.file_type)
    if (!fileAccess.download) return false
    if (fileAccess.files_scope !== 'own') {
      return true
    }
    const userId = this.props.currentUserId
    return userId != null && item.author_id === userId
  }

  canViewFile(item) {
    const fileAccess = this.getFileAccess(item?.file_type)
    if (!fileAccess.view) return false
    if (fileAccess.files_scope !== 'own') return true
    const userId = this.props.currentUserId
    return userId != null && item.author_id === userId
  }

  getFileAccess(fileType) {
    const byType = this.props.fileAccessByType
    let fa = this.props.fileAccess
    if (fileType && byType && typeof byType === 'object' && byType[fileType]) {
      fa = byType[fileType]
    }
    if (fa && typeof fa === 'object') {
      return {
        read: fa.read !== false,
        upload: !!fa.upload,
        view: fa.view !== false,
        download: fa.download !== false,
        delete: !!fa.delete,
        files_scope: fa.files_scope || 'all',
      }
    }
    const viewOnly = !!this.props.viewOnly
    return {
      read: true,
      upload: !viewOnly,
      view: true,
      download: true,
      delete: !viewOnly,
      files_scope: 'all',
    }
  }

  canDeleteFile(item) {
    if (this.props.canDelete === false) return false
    const fileAccess = this.getFileAccess(item?.file_type)
    const useFileAccessRules = this.props.fileAccess && typeof this.props.fileAccess === 'object'
    if (useFileAccessRules) {
      if (!fileAccess.delete) return false
    } else if (this.props.canDelete !== true) {
      return false
    }
    if (fileAccess.files_scope !== 'own') {
      return true
    }
    const userId = this.props.currentUserId
    return userId != null && item.author_id === userId
  }

  renderFile(item) {
    const { getChoice } = this.props
    const fileAccess = this.getFileAccess(item.file_type)
    const viewOnly = !fileAccess.upload
    const { selectedFiles } = this.state
    const selected = selectedFiles.some((f) => f.id === item.id)

    const thumb =
      item.thumbnail && !item.thumbnail.includes('dummy') ? (
        <img src={`/media/${item.thumbnail}`} alt="" className="sigur-files__file-thumb" />
      ) : (
        <span className="ti ti-file-description sigur-files__file-icon" aria-hidden />
      )
    const validErrorText = this.state.errorEditedFields?.[item.id]
    const schema = this.getSchemaByFiletype(item.file_type)
    const { showDateStart, showDateEnd } = this.getDateFieldFlags(schema)
    const showDateFields = this.props.isRenderDataFields && (showDateStart || showDateEnd)

    return (
      <div>
        <div
          key={item.id}
          className={`sigur-files__file-row ${!viewOnly && selected ? 'sigur-files__file-row--selected' : ''}`}
          onClick={() => this.canViewFile(item) && this.openPreview(item)}
          style={fileAccess.view ? undefined : { cursor: 'default' }}
        >
          <div className="sigur-files__file-thumb-wrap">
            {thumb}
          </div>
          <div className="sigur-files__file-info">
            <span className="sigur-files__file-name" title={item.file_name}>
              {item.file_name}
            </span>
            <span className="sigur-files__file-meta">
              {format_datetime(item.d_create)}
              {getChoice && (
                <> · {getChoice('User', item.author_id)}</>
              )}
            </span>
          </div>
          <div className="sigur-files__file-actions" onClick={(e) => e.stopPropagation()}>
            {this.canViewFile(item) && (
              <button
                type="button"
                className="sigur-files__file-btn"
                onClick={() => this.openPreview(item)}
                title="Просмотр"
              >
                <i className="ti ti-eye" aria-hidden />
              </button>
            )}
            {fileAccess.download && this.canDownloadFile(item) && (
              <button
                type="button"
                className="sigur-files__file-btn"
                onClick={() => this.downloadFiles(item)}
                title="Скачать"
              >
                <i className="ti ti-download" aria-hidden />
              </button>
            )}
            {this.canDeleteFile(item) && (
              <button
                type="button"
                className="sigur-files__file-btn"
                onClick={() => this.deleteFile([item.id])}
                title="Удалить"
              >
                <i className="ti ti-trash" aria-hidden />
              </button>
            )}
            {!viewOnly && (
              <button
                type="button"
                className={`sigur-files__file-btn sigur-files__file-btn--check ${selected ? 'sigur-files__file-btn--checked' : ''}`}
                onClick={() => this.toggleFileSelect(item)}
                title={selected ? 'Снять выбор' : 'Выбрать'}
              >
                <i className={`ti ti-${selected ? 'square-check' : 'square'}`} aria-hidden />
              </button>
            )}
          </div>
        </div>
        {showDateFields && (
            <div>
          <div className="sigur-files__file-row">
            {showDateStart && (
            <div>
              <label className="form-label small mb-1">Дата начала</label>
              <AttrInput
                data_type="date"
                name="d_start"
                access={viewOnly ? "read" : "edit"}
                value={this.getFileFieldValue(item, 'd_start')}
                onChange={(_, value) => { this.handleFileFieldChange(item.id, 'd_start', value) }}
              />
            </div>
            )}
            {showDateEnd && (
            <div>
              <label className="form-label small mb-1">Дата окончания</label>
              <AttrInput
                data_type="date"
                name="d_end"
                access={viewOnly ? "read" : "edit"}
                value={this.getFileFieldValue(item, 'd_end')}
                onChange={(_, value) => { this.handleFileFieldChange(item.id, 'd_end', value) }}
              />
            </div>
            )}
          </div>
            {validErrorText && (
                <div className="text-danger sigur-files__file-row">{validErrorText}</div>
            )}
          </div>
        )}
      </div>
    )
  }

  isSectionExpanded(filetype) {
    if (!this.props.isRenderChevron) return true
    const { expandedTypes } = this.state
    const list = expandedTypes === null ? (this.props.files_schema || []).filter((i) => i.filetype !== undefined).map((i) => i.filetype) : expandedTypes
    return list.includes(filetype)
  }

  handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter++;

    if (this.dragCounter === 1) {
      this.setState({ dragOverType: e.currentTarget.dataset.filetype });
    }
  }

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) {
      return;
    }

    this.dragCounter = 0;
    this.setState({ dragOverType: null });
  }

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter = 0;
    this.setState({ dragOverType: null });

    const files = e.dataTransfer.files;
    if (files?.length) {
      this.uploadHandler(e.currentTarget.dataset.filetype, files);
    }
  }

  renderTypeSection(item) {
    if (item.filetype === undefined) {
      return (
        <div key={item.title} className="sigur-files__group-title">
          {item.title}
        </div>
      )
    }

    const docTypeId = item.doc_type_id ?? item.docTypeId
    const validityIcon = this.renderValidityIcon(this.getIsValid(docTypeId))
    const requiredBadge = item.is_required ? (
      <span
        className="ms-2 d-inline-flex align-items-center"
        onMouseEnter={() => this.setState({ requiredPopoverKey: item.filetype })}
        onMouseLeave={() => this.setState({ requiredPopoverKey: null })}
      >
        <SigurPopover
          placement="top"
          visible={this.state.requiredPopoverKey === item.filetype}
          trigger={
            <span className="text-danger d-inline-flex align-items-center lh-1" aria-label="Обязательный документ">
              <i className="ti ti-alert-circle lh-1" aria-hidden />
            </span>
          }
        >
          Обязательный документ
        </SigurPopover>
      </span>
    ) : null

    const fileList = this.getFilesByType(item.filetype)
    
    if (this.props.viewOnly && fileList.length === 0) {
      return null
    }

    const selectedInType = this.getFilesByType(item.filetype, this.state.selectedFiles)
    const expanded = this.isSectionExpanded(item.filetype)
    const allSelected = fileList.length > 0 && selectedInType.length === fileList.length
    const fileAccess = this.getFileAccess(item.filetype)
    if (!fileAccess.read) {
      return null
    }
    const viewOnly = !fileAccess.upload
    const isRenderChevron = this.props.isRenderChevron
    return (
      <div
        key={`${item.filetype}-${item.title}`}
        data-filetype={item.filetype}
        className={`
          sigur-files__section
          ${this.state.dragOverType === item.filetype ? 'sigur-files__section--drag' : ''}
        `}
        onDragEnter={this.handleDragEnter}
        onDragOver={this.handleDragOver}
        onDragLeave={this.handleDragLeave}
        onDrop={this.handleDrop}
      >
        <div
          className="sigur-files__section-header"
          onClick={() => isRenderChevron && this.toggleExpanded(item.filetype)}
          style={!isRenderChevron ? { cursor: 'default' } : undefined}
        >
          {isRenderChevron && this.props.key !== 'request_files' && (
            <i className={`ti ti-chevron-${expanded ? 'down' : 'right'} sigur-files__section-chevron`} aria-hidden />
          )}
          <span className="sigur-files__section-title d-inline-flex align-items-center text-break">
            {item.title}
            {validityIcon}
            {requiredBadge}
          </span>
          <span className="sigur-files__section-count">{fileList.length}</span>
          {!viewOnly && (
            <div className="sigur-files__section-actions" onClick={(e) => e.stopPropagation()}>
              {fileList.length > 0 && (
                <button
                  type="button"
                  className={`sigur-files__section-btn ${allSelected ? 'sigur-files__section-btn--checked' : ''}`}
                  onClick={() => this.toggleTypeSelect(item.filetype)}
                  title={allSelected ? 'Снять выбор' : 'Выбрать все'}
                >
                  <i className={`ti ti-${allSelected ? 'square-check' : 'square'}`} aria-hidden />
                </button>
              )}
              <button
                type="button"
                className="sigur-files__upload-btn"
                onClick={() => this.uploadHandler(item.filetype)}
              >
                <i className="ti ti-upload" aria-hidden />
                Загрузить
              </button>
            </div>
          )}
        </div>
        {expanded && (
          <div className="sigur-files__section-body">
            {fileList.length === 0 ? (
              <div className="sigur-files__empty">
                <i className="ti ti-folder-off" aria-hidden />
                <span>Нет файлов</span>
              </div>
            ) : (
              <div className="sigur-files__list">
                {fileList.map(this.renderFile)}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  getVisibleFiles(files) {
    const userId = this.props.currentUserId
    return (files || []).filter((f) => {
      const fileAccess = this.getFileAccess(f.file_type)
      if (!fileAccess.read) return false
      if (fileAccess.files_scope === 'own' && userId != null) {
        return f.author_id === userId
      }
      return fileAccess.files_scope === 'all'
    })
  }

  render() {
    const fileAccess = this.getFileAccess()
    if (!fileAccess.read) {
      return null
    }
    const { files_schema = [], customHeader } = this.props
    const files = this.getVisibleFiles(this.props.files)
    const viewOnly = !fileAccess.upload
    const { selectedFiles } = this.state
    const hasFiles = files.length > 0
    const allSelected = hasFiles && selectedFiles.length === files.length
    const previewFile = this.state.previewFileId
      ? files.find((f) => f.id === this.state.previewFileId)
      : null

    const blockExpanded = this.state.blockExpanded
    const collapseIconClass = blockExpanded ? 'ti-chevron-down' : 'ti-chevron-left'

    const visibleSections =
      viewOnly && files_schema.length > 0
        ? files_schema.filter((item) => item.filetype === undefined || this.getFilesByType(item.filetype).length > 0)
        : files_schema

    const viewOnlyHasAnyFiles =
      viewOnly &&
      files_schema.some((item) => item.filetype !== undefined && this.getFilesByType(item.filetype).length > 0)

    return (
      <div className="card card-action mb-3 sigur-files">
        <div className="card-header py-2 px-3">
          <h5 className="card-action-title mb-0">{customHeader || 'Файлы'}</h5>
          <div className="card-action-element d-flex align-items-center gap-2">
            {hasFiles && fileAccess.download && (
              <>
                {!viewOnly && (
                  <button
                    type="button"
                    className={`align-self-center btn btn-sm btn-link p-0 shadow-none ${allSelected ? 'sigur-files__toolbar-checked' : ''}`}
                    onClick={this.toggleSelectAll}
                  >
                    <i className={`ti ti-${allSelected ? 'square-check' : 'square'} me-1`} aria-hidden />
                    Выбрать все
                  </button>
                )}
                <button
                  type="button"
                  className="sigur-files__download-btn"
                  onClick={() =>
                    this.downloadFiles(...(selectedFiles.length > 0 ? selectedFiles : files))
                  }
                >
                  <i className="ti ti-download me-1" aria-hidden />
                  Скачать
                </button>
              </>
            )}
            <ul className="list-inline mb-0 ms-auto">
              <li className="list-inline-item">
                <span className="card-collapsible pointer" onClick={this.toggleBlockExpand} aria-expanded={blockExpanded}>
                  <i className={`tf-icons ti scaleX-n1-rtl ti-sm ${collapseIconClass}`} aria-hidden />
                </span>
              </li>
            </ul>
          </div>
        </div>
        {blockExpanded && (
          <div className="card-body p-0">
            {files_schema.length === 0 ? (
              <div className="sigur-files__empty-state p-3">
                <i className="ti ti-folder-off me-2" aria-hidden />
                <span className="text-muted">Нет настроенных типов файлов</span>
              </div>
            ) : viewOnly && files_schema.length > 0 && !viewOnlyHasAnyFiles ? (
              <div className="sigur-files__empty-state p-3">
                <i className="ti ti-folder-off me-2" aria-hidden />
                <span className="text-muted">Нет файлов</span>
              </div>
            ) : (
              <div className="sigur-files__sections">
                {visibleSections.map((item) => this.renderTypeSection(item))}
              </div>
            )}
          </div>
        )}

        {previewFile && <FilePreviewModal file={previewFile} getChoice={this.props.getChoice} />}
        {!viewOnly && (
          <input
            ref={this.refFileInput}
            type="file"
            multiple
            className="d-none"
            aria-hidden
          />
        )}
      </div>
    )
  }
}
